import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  BuildingOffice2Icon,
  MagnifyingGlassIcon,
  MapPinIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  PrinterIcon,
  CameraIcon,
} from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { Input, Textarea } from '../../components/Forms';
import ScanModal from '../../components/Boarding/ScanModal';
import { useAuth } from '../../contexts/AuthContext';
import { hasModulePermission } from '../../utils/permissions';
import type { HousingLookupResult, Trip } from '../../types';
import './housing-report-print.css';

type RecipientKey = 'contact' | 'office' | 'rep';
type ReportRecipientKey = 'office' | 'rep' | 'driver' | 'custom';

interface HousingReportRoom {
  roomNumber: string;
  hotelName?: string;
  beds: Array<{
    name: string;
    phone?: string;
    bookingNumber?: string;
    organization?: string;
  }>;
}

interface HousingReportMeta {
  roomsCount: number;
  bedsCount: number;
  bookingsCount: number;
  tripNumber?: string;
  date?: string;
  companyName?: string;
  departure?: { date?: string; time?: string; day?: string };
  bus?: {
    busId?: string;
    busNumber?: string;
    driverName?: string;
    driverPhone?: string;
  };
  rooms: HousingReportRoom[];
}

interface HousingListItem {
  _id: string;
  bookingNumber: string;
  contactPerson: { name?: string; phone?: string; roomNumber?: string };
  totalPeople: number;
  roomsAssigned: number;
  housingComplete: boolean;
  housing?: {
    locationText?: string;
    mapsUrl?: string;
    hotelName?: string;
  };
  assignedAccommodation?: {
    _id: string;
    name: string;
    city?: string;
    address?: string;
  } | null;
}

const HousingPage: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const canUpdate = hasModulePermission(user, 'housing', 'update');
  const isHotelRep = user?.role === 'hotel_rep';

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [city, setCity] = useState('');
  const [complete, setComplete] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showNotify, setShowNotify] = useState(false);
  const [recipients, setRecipients] = useState<Record<RecipientKey, boolean>>({
    contact: true,
    office: false,
    rep: false,
  });
  const [messageText, setMessageText] = useState('');
  const [isEditingMessage, setIsEditingMessage] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [actionErr, setActionErr] = useState('');

  const [hotelName, setHotelName] = useState('');
  const [locationText, setLocationText] = useState('');
  const [mapsUrl, setMapsUrl] = useState('');
  const [contactRoom, setContactRoom] = useState('');
  const [companionRooms, setCompanionRooms] = useState<
    Array<{ companionId?: string; index: number; name: string; roomNumber: string }>
  >([]);

  const [showReport, setShowReport] = useState(false);
  const [reportTripId, setReportTripId] = useState('');
  const [reportBusId, setReportBusId] = useState('');
  const [reportRecipients, setReportRecipients] = useState<
    Record<ReportRecipientKey, boolean>
  >({
    office: true,
    rep: false,
    driver: false,
    custom: false,
  });
  const [reportCustomPhone, setReportCustomPhone] = useState('');
  const [reportMessage, setReportMessage] = useState('');
  const [isEditingReport, setIsEditingReport] = useState(false);
  const [reportPreviewLoading, setReportPreviewLoading] = useState(false);
  const [reportPreviewError, setReportPreviewError] = useState('');
  const [reportMeta, setReportMeta] = useState<HousingReportMeta | null>(null);

  const [showScanner, setShowScanner] = useState(false);
  const [scanLookup, setScanLookup] = useState<HousingLookupResult | null>(null);
  const [scanError, setScanError] = useState('');
  const [isScanningLookup, setIsScanningLookup] = useState(false);

  const canSendReport =
    hasModulePermission(user, 'housing', 'update') ||
    hasModulePermission(user, 'housing', 'export');

  const resetScan = useCallback(() => {
    setScanLookup(null);
    setScanError('');
  }, []);

  const openScanner = useCallback(() => {
    resetScan();
    setShowScanner(true);
  }, [resetScan]);

  const closeScanner = useCallback(() => {
    setShowScanner(false);
    resetScan();
    if (searchParams.get('scan') === '1') {
      const next = new URLSearchParams(searchParams);
      next.delete('scan');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, resetScan]);

  useEffect(() => {
    if (searchParams.get('scan') === '1') {
      openScanner();
    }
  }, [searchParams, openScanner]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['housing', page, search, city, complete],
    queryFn: () =>
      apiService.getHousingBookings(page, 20, {
        search: search || undefined,
        city: city || undefined,
        complete: complete || undefined,
      }),
    placeholderData: (prev) => prev,
  });

  const {
    data: tripsData,
    isLoading: tripsLoading,
    isError: tripsError,
    error: tripsQueryError,
  } = useQuery({
    queryKey: ['housing-report-trips'],
    queryFn: () => apiService.getTrips(1, 100, {}),
    enabled: showReport,
  });

  const {
    data: detail,
    isLoading: detailLoading,
    refetch: refetchDetail,
  } = useQuery({
    queryKey: ['housing-detail', selectedId],
    queryFn: () => apiService.getHousingBooking(selectedId!),
    enabled: !!selectedId,
  });

  useEffect(() => {
    if (!detail) return;
    setHotelName(
      detail.housing?.hotelName ||
        detail.assignedAccommodation?.name ||
        ''
    );
    setLocationText(detail.housing?.locationText || '');
    setMapsUrl(detail.housing?.mapsUrl || '');
    setContactRoom(detail.contactPerson?.roomNumber || '');
    const companions = (detail.companions || []).map((c: any, index: number) => ({
      companionId: c._id ? String(c._id) : undefined,
      index,
      name: c.name || `مرافق ${index + 1}`,
      roomNumber: c.roomNumber || '',
    }));
    setCompanionRooms(companions);
    setActionMsg('');
    setActionErr('');
  }, [detail]);

  const saveMutation = useMutation({
    mutationFn: () =>
      apiService.updateHousing(selectedId!, {
        housing: { hotelName, locationText, mapsUrl },
        contactRoomNumber: contactRoom,
        companionRooms: companionRooms.map((c) => ({
          companionId: c.companionId,
          index: c.index,
          roomNumber: c.roomNumber,
        })),
      }),
    onSuccess: (res) => {
      setActionMsg(res.message || 'تم الحفظ');
      setActionErr('');
      queryClient.invalidateQueries({ queryKey: ['housing'] });
      refetchDetail();
    },
    onError: (err: any) => {
      setActionErr(err?.response?.data?.message || err?.message || 'فشل الحفظ');
      setActionMsg('');
    },
  });

  const notifyMutation = useMutation({
    mutationFn: (payload: { selected: RecipientKey[]; message: string }) =>
      apiService.notifyHousing(selectedId!, payload.selected, payload.message),
    onSuccess: (res) => {
      setActionMsg(res.message || 'تم الإرسال');
      setActionErr('');
      setShowNotify(false);
      setIsEditingMessage(false);
      setMessageText('');
      queryClient.invalidateQueries({ queryKey: ['housing'] });
      refetchDetail();
    },
    onError: (err: any) => {
      setActionErr(
        err?.response?.data?.message || err?.message || 'فشل إرسال الواتساب'
      );
      setActionMsg('');
    },
  });

  const sendReportMutation = useMutation({
    mutationFn: (payload: {
      tripId: string;
      busId?: string;
      selected: ReportRecipientKey[];
      customPhone?: string;
      message: string;
    }) =>
      apiService.sendHousingReport({
        tripId: payload.tripId,
        busId: payload.busId,
        recipients: payload.selected,
        customPhone: payload.customPhone,
        message: payload.message,
      }),
    onSuccess: (res) => {
      setActionMsg(res.message || 'تم إرسال تقرير التسكين');
      setActionErr('');
      setShowReport(false);
      setIsEditingReport(false);
      setReportMessage('');
      setReportMeta(null);
    },
    onError: (err: any) => {
      setActionErr(
        err?.response?.data?.message ||
          err?.message ||
          'فشل إرسال تقرير التسكين'
      );
      setActionMsg('');
    },
  });

  const items: HousingListItem[] = data?.data || [];
  const pagination = data?.pagination;
  const trips: Trip[] = tripsData?.data || [];

  const selectedTrip = useMemo(
    () => trips.find((t) => t._id === reportTripId) || null,
    [trips, reportTripId]
  );

  const tripBuses = useMemo(() => {
    const list = selectedTrip?.buses || [];
    return list
      .map((entry) => {
        const bus = typeof entry.bus === 'object' ? entry.bus : null;
        const busId = bus?._id || (entry.bus as unknown as string) || '';
        if (!busId) return null;
        return {
          busId: String(busId),
          label: `${bus?.busNumber || 'باص'} — ${entry.driver?.name || 'بدون سائق'}`,
        };
      })
      .filter(Boolean) as Array<{ busId: string; label: string }>;
  }, [selectedTrip]);

  const selectedRecipients = useMemo(
    () =>
      (Object.keys(recipients) as RecipientKey[]).filter((k) => recipients[k]),
    [recipients]
  );

  const selectedReportRecipients = useMemo(
    () =>
      (Object.keys(reportRecipients) as ReportRecipientKey[]).filter(
        (k) => reportRecipients[k]
      ),
    [reportRecipients]
  );

  const openDetail = (id: string) => {
    setSelectedId(id);
    setShowNotify(false);
    setIsEditingMessage(false);
    setMessageText('');
    setPreviewError('');
  };

  const handleHousingLookup = useCallback(async (rawToken: string) => {
    const token = rawToken.trim();
    if (!token) {
      setScanError('أدخل أو امسح رمز الصعود');
      return;
    }
    setIsScanningLookup(true);
    setScanError('');
    setScanLookup(null);
    setActionErr('');
    try {
      const data = await apiService.housingLookup(token);
      setScanLookup(data);
    } catch (err: any) {
      setScanError(
        err?.response?.data?.message || err?.message || 'تعذر العثور على الزائر'
      );
    } finally {
      setIsScanningLookup(false);
    }
  }, []);


  const closeDetail = () => {
    setSelectedId(null);
    setShowNotify(false);
    setIsEditingMessage(false);
    setMessageText('');
    setPreviewError('');
  };

  const openNotifyModal = async () => {
    if (!selectedId || !canUpdate) return;
    setShowNotify(true);
    setIsEditingMessage(false);
    setPreviewError('');
    setPreviewLoading(true);
    setActionErr('');
    try {
      // Always save current form (rooms/location) before building the WhatsApp text
      await apiService.updateHousing(selectedId, {
        housing: { hotelName, locationText, mapsUrl },
        contactRoomNumber: contactRoom,
        companionRooms: companionRooms.map((c) => ({
          companionId: c.companionId,
          index: c.index,
          roomNumber: c.roomNumber,
        })),
      });
      queryClient.invalidateQueries({ queryKey: ['housing'] });
      await refetchDetail();

      const preview = await apiService.getHousingMessagePreview(selectedId);
      setMessageText(preview.message || '');
      if (!preview.housingComplete) {
        setActionMsg(
          'تنبيه: تأكد أن أرقام الغرف والموقع محفوظة — قد تظهر غرف ناقصة في الرسالة.'
        );
      }
    } catch (err: any) {
      setMessageText('');
      setPreviewError(
        err?.response?.data?.message ||
          err?.message ||
          'تعذر تحميل صيغة الرسالة. احفظ التسكين ثم أعد المحاولة.'
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  const closeNotifyModal = () => {
    setShowNotify(false);
    setIsEditingMessage(false);
    setPreviewError('');
  };

  const openReportModal = () => {
    setShowReport(true);
    setReportTripId('');
    setReportBusId('');
    setReportMessage('');
    setReportMeta(null);
    setReportPreviewError('');
    setIsEditingReport(false);
    setActionErr('');
    setActionMsg('');
  };

  const closeReportModal = () => {
    setShowReport(false);
    setIsEditingReport(false);
    setReportPreviewError('');
  };

  const loadReportPreview = async (tripId: string, busId?: string) => {
    if (!tripId) return;
    setReportPreviewLoading(true);
    setReportPreviewError('');
    setIsEditingReport(false);
    try {
      const preview = await apiService.getHousingReportPreview(tripId, busId);
      setReportMessage(preview.message || '');
      setReportMeta({
        roomsCount: preview.roomsCount || 0,
        bedsCount: preview.bedsCount || 0,
        bookingsCount: preview.bookingsCount || 0,
        tripNumber: preview.tripNumber,
        date: preview.date,
        companyName: preview.companyName,
        departure: preview.departure,
        bus: preview.bus,
        rooms: preview.rooms || [],
      });
    } catch (err: any) {
      setReportMessage('');
      setReportMeta(null);
      setReportPreviewError(
        err?.response?.data?.message ||
          err?.message ||
          'تعذر تحميل تقرير التسكين'
      );
    } finally {
      setReportPreviewLoading(false);
    }
  };

  const handlePrintReport = () => {
    if (!reportMeta?.rooms?.length && !reportMessage.trim()) return;
    setTimeout(() => window.print(), 100);
  };

  const departureLabel = useMemo(() => {
    if (!reportMeta?.departure) return '—';
    const parts = [
      reportMeta.departure.day,
      reportMeta.departure.date,
      reportMeta.departure.time ? `الساعة ${reportMeta.departure.time}` : '',
    ].filter(Boolean);
    return parts.join(' — ') || '—';
  }, [reportMeta]);

  const renderHousingReportSheet = (forPrint = false) => {
    if (!reportMeta) return null;
    const hotels = Array.from(
      new Set(
        (reportMeta.rooms || [])
          .map((r) => r.hotelName)
          .filter((h): h is string => !!h)
      )
    );

    return (
      <div
        className={
          forPrint
            ? 'housing-report-print-sheet'
            : 'rounded-xl border border-gray-200 bg-white overflow-hidden'
        }
      >
        <div className={forPrint ? 'housing-report-header' : 'px-4 pt-4 pb-3 border-b border-gray-100'}>
          <h1 className={forPrint ? 'housing-report-print-title' : 'text-lg font-bold text-gray-900'}>
            كشف التسكين
          </h1>
          <p className={forPrint ? 'housing-report-print-sub' : 'text-sm text-gray-500 mt-0.5'}>
            رحلة {reportMeta.tripNumber || '—'}
            {reportMeta.departure?.day ? ` · يوم ${reportMeta.departure.day}` : ''}
            {reportMeta.departure?.date || reportMeta.date
              ? ` · تاريخ ${reportMeta.departure?.date || reportMeta.date}`
              : ''}
          </p>
        </div>

        <div
          className={
            forPrint
              ? 'housing-report-info-grid'
              : 'grid grid-cols-2 sm:grid-cols-3 gap-3 px-4 py-3 bg-gray-50/80 text-sm'
          }
        >
          <div className={forPrint ? 'housing-report-info-item' : ''}>
            <span className={forPrint ? 'housing-report-info-label' : 'block text-xs text-gray-500'}>
              الشركة
            </span>
            <span className={forPrint ? 'housing-report-info-value' : 'font-medium text-gray-900'}>
              {reportMeta.companyName || '—'}
            </span>
          </div>
          <div className={forPrint ? 'housing-report-info-item' : ''}>
            <span className={forPrint ? 'housing-report-info-label' : 'block text-xs text-gray-500'}>
              السائق
            </span>
            <span className={forPrint ? 'housing-report-info-value' : 'font-medium text-gray-900'}>
              {reportMeta.bus?.driverName || '—'}
            </span>
          </div>
          <div className={forPrint ? 'housing-report-info-item' : ''}>
            <span className={forPrint ? 'housing-report-info-label' : 'block text-xs text-gray-500'}>
              رقم الباص
            </span>
            <span className={forPrint ? 'housing-report-info-value' : 'font-medium text-gray-900'}>
              {reportMeta.bus?.busNumber || '—'}
            </span>
          </div>
          <div className={forPrint ? 'housing-report-info-item' : ''}>
            <span className={forPrint ? 'housing-report-info-label' : 'block text-xs text-gray-500'}>
              الفندق
            </span>
            <span className={forPrint ? 'housing-report-info-value' : 'font-medium text-gray-900'}>
              {hotels.join('، ') || '—'}
            </span>
          </div>
          <div className={forPrint ? 'housing-report-info-item' : 'sm:col-span-2'}>
            <span className={forPrint ? 'housing-report-info-label' : 'block text-xs text-gray-500'}>
              موعد المغادرة من الغرفة
            </span>
            <span className={forPrint ? 'housing-report-info-value' : 'font-medium text-gray-900'}>
              {departureLabel}
            </span>
          </div>
        </div>

        <div className={forPrint ? 'housing-report-counts' : 'flex flex-wrap gap-2 px-4 py-2 text-xs text-gray-600 border-b border-gray-100'}>
          <span>{reportMeta.bookingsCount} حجز</span>
          <span>·</span>
          <span>{reportMeta.roomsCount} غرفة</span>
          <span>·</span>
          <span>{reportMeta.bedsCount} سرير</span>
        </div>

        {(reportMeta.rooms || []).length === 0 ? (
          <div className={forPrint ? 'housing-report-empty' : 'px-4 py-8 text-center text-sm text-gray-500'}>
            لا توجد بيانات تسكين لهذه الرحلة
          </div>
        ) : (
          <div className={forPrint ? 'housing-report-rooms' : 'divide-y divide-gray-100'}>
            {reportMeta.rooms.map((room) => (
              <div key={room.roomNumber} className={forPrint ? 'housing-report-room' : 'px-4 py-3'}>
                <div className={forPrint ? 'housing-report-room-head' : 'flex items-center justify-between gap-2 mb-2'}>
                  <h3 className={forPrint ? 'housing-report-room-title' : 'text-sm font-semibold text-gray-900'}>
                    غرفة {room.roomNumber}
                  </h3>
                  <span className={forPrint ? 'housing-report-room-meta' : 'text-xs text-gray-500'}>
                    عدد الأسرة: {room.beds.length}
                    {room.hotelName ? ` · ${room.hotelName}` : ''}
                  </span>
                </div>
                <table className={forPrint ? 'housing-report-table' : 'w-full text-sm'}>
                  <thead>
                    <tr className={forPrint ? '' : 'bg-gray-50 text-gray-500'}>
                      <th className={forPrint ? '' : 'text-right px-2 py-1.5 font-medium w-16'}>
                        السرير
                      </th>
                      <th className={forPrint ? '' : 'text-right px-2 py-1.5 font-medium'}>
                        الاسم
                      </th>
                      <th className={forPrint ? '' : 'text-right px-2 py-1.5 font-medium w-28'}>
                        رقم الحجز
                      </th>
                      <th className={forPrint ? '' : 'text-right px-2 py-1.5 font-medium w-28 hidden sm:table-cell'}>
                        الجوال
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {room.beds.map((bed, idx) => (
                      <tr key={`${room.roomNumber}-${idx}`} className={forPrint ? '' : 'border-t border-gray-100'}>
                        <td className={forPrint ? 'bed-num' : 'px-2 py-1.5 text-gray-700'}>
                          {idx + 1}
                        </td>
                        <td className={forPrint ? 'bed-name' : 'px-2 py-1.5 font-medium text-gray-900'}>
                          {bed.name}
                        </td>
                        <td className={forPrint ? 'bed-booking' : 'px-2 py-1.5 text-gray-600'}>
                          {bed.bookingNumber ? `#${bed.bookingNumber}` : '—'}
                        </td>
                        <td className={forPrint ? 'bed-phone' : 'px-2 py-1.5 text-gray-500 hidden sm:table-cell'}>
                          {bed.phone || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">خطأ في تحميل بيانات التسكين</div>
        <button type="button" onClick={() => refetch()} className="btn-primary">
          إعادة المحاولة
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BuildingOffice2Icon className="h-7 w-7 text-primary-600" />
            التسكين
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {isHotelRep
              ? 'امسح رمز الزائر لعرض بياناته وسكنه'
              : 'تعيين أرقام الغرف وموقع الفندق وإرسال كشف التسكين عبر واتساب'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 self-start">
          <button
            type="button"
            className="btn-primary inline-flex items-center gap-2"
            onClick={openScanner}
          >
            <CameraIcon className="h-5 w-5" />
            تصوير QR
          </button>
          {canSendReport && (
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50"
              onClick={openReportModal}
            >
              <DocumentTextIcon className="h-5 w-5" />
              إرسال تقرير التسكين
            </button>
          )}
        </div>
      </div>

      {actionMsg && !selectedId && (
        <div className="rounded-lg bg-green-50 text-green-800 text-sm px-3 py-2">
          {actionMsg}
        </div>
      )}
      {actionErr && !selectedId && (
        <div className="rounded-lg bg-red-50 text-red-800 text-sm px-3 py-2">
          {actionErr}
        </div>
      )}

      {/* QR scan popup — one read, then data + actions */}
      <ScanModal
        open={showScanner}
        onClose={closeScanner}
        title="تصوير رمز الزائر"
        subtitle="وجّه الكاميرا على QR الصعود لعرض بيانات الزائر والسكن"
        elementId="housing-qr-reader"
        onLookup={handleHousingLookup}
        isLookingUp={isScanningLookup}
        error={scanError}
        hasResult={!!scanLookup}
        onReset={resetScan}
      >
        {scanLookup && (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs text-primary-700 mb-0.5">الزائر</p>
                <p className="text-lg font-bold text-gray-900">
                  {scanLookup.passenger.name}
                </p>
                <p className="text-sm text-gray-600">
                  حجز #{scanLookup.booking.bookingNumber}
                  {scanLookup.passenger.isMainContact
                    ? ' · المسؤول'
                    : ' · مرافق'}
                </p>
              </div>
              <div className="text-left shrink-0">
                <p className="text-xs text-gray-500">الغرفة</p>
                <p className="text-2xl font-bold text-primary-700 tracking-wide">
                  {scanLookup.passenger.roomNumber || '—'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-gray-500">الهوية</span>
                <p className="font-medium">
                  {scanLookup.passenger.idNumber || '—'}
                </p>
              </div>
              <div>
                <span className="text-gray-500">الجوال</span>
                <p className="font-medium">
                  {scanLookup.passenger.phone || '—'}
                </p>
              </div>
              <div>
                <span className="text-gray-500">الفندق</span>
                <p className="font-medium">
                  {scanLookup.housing.hotelName ||
                    scanLookup.assignedAccommodation?.name ||
                    '—'}
                </p>
              </div>
              <div>
                <span className="text-gray-500">حالة التسكين</span>
                <p
                  className={`font-medium ${
                    scanLookup.housing.complete
                      ? 'text-green-700'
                      : 'text-amber-700'
                  }`}
                >
                  {scanLookup.housing.complete ? 'مكتمل' : 'غير مكتمل'}
                </p>
              </div>
              {scanLookup.housing.locationText && (
                <div className="sm:col-span-2">
                  <span className="text-gray-500">الموقع</span>
                  <p className="font-medium flex items-start gap-1.5">
                    <MapPinIcon className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                    {scanLookup.housing.locationText}
                  </p>
                </div>
              )}
              {scanLookup.housing.mapsUrl && (
                <div className="sm:col-span-2">
                  <a
                    href={scanLookup.housing.mapsUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary-600 underline text-sm"
                  >
                    فتح الموقع على الخريطة
                  </a>
                </div>
              )}
            </div>

            {(scanLookup.passengers || []).length > 1 && (
              <div className="border-t border-primary-100 pt-3">
                <p className="text-xs text-gray-500 mb-2">غرف المجموعة</p>
                <ul className="space-y-1 text-sm">
                  {scanLookup.passengers.map((p) => (
                    <li
                      key={p.key}
                      className="flex items-center justify-between gap-2"
                    >
                      <span className="text-gray-800">{p.name}</span>
                      <span className="font-semibold text-gray-900">
                        {p.roomNumber ? `غرفة ${p.roomNumber}` : '—'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <button
              type="button"
              onClick={() => {
                if (scanLookup.booking?.id) {
                  const id = String(scanLookup.booking.id);
                  closeScanner();
                  openDetail(id);
                }
              }}
              className="btn-primary w-full"
            >
              فتح تفاصيل التسكين
            </button>
          </div>
        )}
      </ScanModal>

      {/* Filters — soft bar */}
      <div className="rounded-2xl border border-gray-100 bg-white/80 shadow-sm px-4 py-4 sm:px-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
          <div className="flex-1 min-w-0">
            <label className="block text-xs font-medium text-gray-500 mb-1.5">
              بحث
            </label>
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="search"
                placeholder="رقم الحجز أو اسم التواصل..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full rounded-xl border border-gray-200 bg-gray-50/80 py-2.5 pr-10 pl-3 text-sm text-gray-800 placeholder:text-gray-400 focus:border-primary-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-100 transition"
              />
            </div>
          </div>

          <div className="w-full lg:w-44">
            <label className="block text-xs font-medium text-gray-500 mb-1.5">
              المدينة
            </label>
            <select
              value={city}
              onChange={(e) => {
                setCity(e.target.value);
                setPage(1);
              }}
              className="w-full rounded-xl border border-gray-200 bg-gray-50/80 py-2.5 px-3 text-sm text-gray-800 focus:border-primary-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-100 transition"
            >
              <option value="">كل المدن</option>
              <option value="مكة المكرمة">مكة المكرمة</option>
              <option value="المدينة المنورة">المدينة المنورة</option>
            </select>
          </div>

          <div className="w-full lg:w-auto">
            <label className="block text-xs font-medium text-gray-500 mb-1.5">
              حالة التسكين
            </label>
            <div className="flex flex-wrap gap-1.5 rounded-xl bg-gray-50/80 border border-gray-100 p-1">
              {[
                { value: '', label: 'الكل' },
                { value: 'false', label: 'ناقص' },
                { value: 'true', label: 'مكتمل' },
              ].map((opt) => {
                const active = complete === opt.value;
                return (
                  <button
                    key={opt.value || 'all'}
                    type="button"
                    onClick={() => {
                      setComplete(opt.value);
                      setPage(1);
                    }}
                    className={`px-3.5 py-1.5 rounded-lg text-sm transition ${
                      active
                        ? 'bg-white text-gray-900 shadow-sm font-medium'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">جاري التحميل...</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-gray-500">لا توجد حجوزات سكن</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">
                    رقم الحجز
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">
                    التواصل
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">
                    الأشخاص
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">
                    الفندق
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">
                    التسكين
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">
                    إجراء
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {items.map((item) => (
                  <tr key={item._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      #{item.bookingNumber}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <div>{item.contactPerson?.name || '—'}</div>
                      <div className="text-xs text-gray-400">
                        {item.contactPerson?.phone || ''}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {item.roomsAssigned}/{item.totalPeople}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <div>
                        {item.housing?.hotelName ||
                          item.assignedAccommodation?.name ||
                          '—'}
                      </div>
                      {item.assignedAccommodation?.city && (
                        <div className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                          <MapPinIcon className="h-3 w-3" />
                          {item.assignedAccommodation.city}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {item.housingComplete ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircleIcon className="h-3.5 w-3.5" />
                          مكتمل
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                          <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                          ناقص
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => openDetail(item._id)}
                        className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                      >
                        تسكين
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pagination && pagination.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="btn-secondary text-sm disabled:opacity-40"
            >
              السابق
            </button>
            <span className="text-sm text-gray-500">
              صفحة {pagination.page} من {pagination.pages}
            </span>
            <button
              type="button"
              disabled={page >= pagination.pages}
              onClick={() => setPage((p) => p + 1)}
              className="btn-secondary text-sm disabled:opacity-40"
            >
              التالي
            </button>
          </div>
        )}
      </div>

      {selectedId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={closeDetail} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                تسكين الحجز
                {detail?.bookingNumber ? ` #${detail.bookingNumber}` : ''}
              </h2>
              <button
                type="button"
                onClick={closeDetail}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                ×
              </button>
            </div>

            <div className="p-5 space-y-5">
              {detailLoading && !detail ? (
                <div className="text-center text-gray-500 py-8">جاري التحميل...</div>
              ) : (
                <>
                  {actionMsg && (
                    <div className="rounded-lg bg-green-50 text-green-800 text-sm px-3 py-2">
                      {actionMsg}
                    </div>
                  )}
                  {actionErr && (
                    <div className="rounded-lg bg-red-50 text-red-800 text-sm px-3 py-2">
                      {actionErr}
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        اسم الفندق
                      </label>
                      <Input
                        value={hotelName}
                        onChange={(e) => setHotelName(e.target.value)}
                        disabled={!canUpdate}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        رابط الخريطة
                      </label>
                      <Input
                        value={mapsUrl}
                        onChange={(e) => setMapsUrl(e.target.value)}
                        disabled={!canUpdate}
                        placeholder="https://maps.google.com/..."
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        موقع الفندق / العنوان
                      </label>
                      <Input
                        value={locationText}
                        onChange={(e) => setLocationText(e.target.value)}
                        disabled={!canUpdate}
                        placeholder="حي، شارع، معلم قريب..."
                      />
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">
                      أرقام الغرف
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 text-sm text-gray-700">
                          {detail?.contactPerson?.name || 'شخص التواصل'}
                          <span className="text-xs text-gray-400 mr-2">(تواصل)</span>
                        </div>
                        <Input
                          className="w-32"
                          placeholder="رقم الغرفة"
                          value={contactRoom}
                          onChange={(e) => setContactRoom(e.target.value)}
                          disabled={!canUpdate}
                        />
                      </div>
                      {companionRooms.map((c) => (
                        <div key={c.index} className="flex items-center gap-3">
                          <div className="flex-1 text-sm text-gray-700">{c.name}</div>
                          <Input
                            className="w-32"
                            placeholder="رقم الغرفة"
                            value={c.roomNumber}
                            onChange={(e) => {
                              const val = e.target.value;
                              setCompanionRooms((prev) =>
                                prev.map((row) =>
                                  row.index === c.index
                                    ? { ...row, roomNumber: val }
                                    : row
                                )
                              );
                            }}
                            disabled={!canUpdate}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {canUpdate && (
                    <div className="flex flex-wrap gap-3 pt-2 border-t">
                      <button
                        type="button"
                        className="btn-primary"
                        disabled={saveMutation.isPending}
                        onClick={() => saveMutation.mutate()}
                      >
                        {saveMutation.isPending ? 'جاري الحفظ...' : 'حفظ التسكين'}
                      </button>
                      <button
                        type="button"
                        className="btn-secondary inline-flex items-center gap-2"
                        onClick={openNotifyModal}
                        disabled={previewLoading || saveMutation.isPending}
                      >
                        <ChatBubbleLeftRightIcon className="h-5 w-5" />
                        {previewLoading ? 'جاري التحضير...' : 'إرسال واتساب'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showNotify && selectedId && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={closeNotifyModal}
          />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-5 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">
              إرسال واتساب التسكين
            </h3>
            <p className="text-sm text-gray-500">
              اختر المستلمين، راجع صيغة الرسالة، وعدّلها إن لزم ثم أكّد الإرسال.
            </p>

            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-800">المستلمون</p>
              {(
                [
                  { key: 'contact' as const, label: 'شخص التواصل' },
                  { key: 'office' as const, label: 'جوال المكتب' },
                  { key: 'rep' as const, label: 'جوال المندوب (حسابي)' },
                ] as const
              ).map((opt) => (
                <label
                  key={opt.key}
                  className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    checked={recipients[opt.key]}
                    onChange={(e) =>
                      setRecipients((prev) => ({
                        ...prev,
                        [opt.key]: e.target.checked,
                      }))
                    }
                  />
                  <span className="text-sm text-gray-800">{opt.label}</span>
                </label>
              ))}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-gray-800">صيغة الرسالة</p>
                {!previewLoading && !previewError && (
                  <button
                    type="button"
                    className="text-sm font-medium text-primary-600 hover:text-primary-800"
                    onClick={() => setIsEditingMessage((v) => !v)}
                  >
                    {isEditingMessage ? 'إنهاء التعديل' : 'تعديل'}
                  </button>
                )}
              </div>

              {previewLoading ? (
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-8 text-center text-sm text-gray-500">
                  جاري تحميل صيغة الرسالة...
                </div>
              ) : previewError ? (
                <div className="rounded-lg bg-red-50 text-red-800 text-sm px-3 py-2">
                  {previewError}
                </div>
              ) : isEditingMessage ? (
                <Textarea
                  rows={12}
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  className="font-mono text-sm leading-relaxed"
                />
              ) : (
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-800 whitespace-pre-wrap leading-relaxed max-h-64 overflow-y-auto">
                  {messageText || '—'}
                </div>
              )}
            </div>

            <div className="flex gap-3 justify-end pt-2">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeNotifyModal}
              >
                إلغاء
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={
                  selectedRecipients.length === 0 ||
                  !messageText.trim() ||
                  previewLoading ||
                  !!previewError ||
                  notifyMutation.isPending
                }
                onClick={() =>
                  notifyMutation.mutate({
                    selected: selectedRecipients,
                    message: messageText,
                  })
                }
              >
                {notifyMutation.isPending
                  ? 'جاري الإرسال...'
                  : 'تأكيد الإرسال'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showReport && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={closeReportModal}
          />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-5 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">
              تقرير التسكين
            </h3>
            <p className="text-sm text-gray-500">
              اختر الرحلة (والباص إن وُجد) لمعاينة كشف التسكين حسب الغرف والأسرة، ثم اطبعه أو أرسله عبر واتساب.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  الرحلة
                </label>
                <select
                  value={reportTripId}
                  onChange={(e) => {
                    const id = e.target.value;
                    setReportTripId(id);
                    setReportBusId('');
                    setReportMessage('');
                    setReportMeta(null);
                    setReportPreviewError('');
                    if (id) loadReportPreview(id);
                  }}
                  disabled={tripsLoading}
                  className="w-full rounded-xl border border-gray-200 bg-gray-50/80 py-2.5 px-3 text-sm text-gray-800 focus:border-primary-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-100 disabled:opacity-60"
                >
                  <option value="">
                    {tripsLoading
                      ? 'جاري تحميل الرحلات...'
                      : 'اختر الرحلة...'}
                  </option>
                  {trips.map((t) => {
                    const route = t.routes?.[0];
                    const routeLabel = route
                      ? `${route.from || ''} → ${route.to || ''}`
                      : '';
                    return (
                      <option key={t._id} value={t._id}>
                        {t.tripNumber}
                        {t.date ? ` — ${String(t.date).slice(0, 10)}` : ''}
                        {routeLabel ? ` — ${routeLabel}` : ''}
                      </option>
                    );
                  })}
                </select>
                {tripsError && (
                  <p className="mt-1.5 text-sm text-red-600">
                    {(tripsQueryError as any)?.message ||
                      'تعذر تحميل الرحلات. تأكد من صلاحية التسكين ثم أعد المحاولة.'}
                  </p>
                )}
                {!tripsLoading && !tripsError && trips.length === 0 && (
                  <p className="mt-1.5 text-sm text-amber-700">
                    لا توجد رحلات متاحة حالياً.
                  </p>
                )}
              </div>

              {tripBuses.length > 1 && (
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    الباص
                  </label>
                  <select
                    value={reportBusId}
                    onChange={(e) => {
                      const id = e.target.value;
                      setReportBusId(id);
                      if (reportTripId) {
                        loadReportPreview(reportTripId, id || undefined);
                      }
                    }}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50/80 py-2.5 px-3 text-sm text-gray-800 focus:border-primary-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-100"
                  >
                    <option value="">كل الباصات / تلقائي</option>
                    {tripBuses.map((b) => (
                      <option key={b.busId} value={b.busId}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {reportMeta && (
              <div className="flex flex-wrap gap-2 text-xs text-gray-600">
                <span className="rounded-full bg-gray-100 px-2.5 py-1">
                  رحلة {reportMeta.tripNumber || '—'}
                </span>
                <span className="rounded-full bg-gray-100 px-2.5 py-1">
                  {reportMeta.bookingsCount} حجز
                </span>
                <span className="rounded-full bg-gray-100 px-2.5 py-1">
                  {reportMeta.roomsCount} غرفة
                </span>
                <span className="rounded-full bg-gray-100 px-2.5 py-1">
                  {reportMeta.bedsCount} سرير
                </span>
              </div>
            )}

            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-800">المستلمون</p>
              {(
                [
                  { key: 'office' as const, label: 'جوال المكتب' },
                  { key: 'rep' as const, label: 'جوال المندوب (حسابي)' },
                  { key: 'driver' as const, label: 'جوال السائق' },
                  { key: 'custom' as const, label: 'رقم مخصص' },
                ] as const
              ).map((opt) => (
                <label
                  key={opt.key}
                  className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    checked={reportRecipients[opt.key]}
                    onChange={(e) =>
                      setReportRecipients((prev) => ({
                        ...prev,
                        [opt.key]: e.target.checked,
                      }))
                    }
                  />
                  <span className="text-sm text-gray-800">{opt.label}</span>
                </label>
              ))}
              {reportRecipients.custom && (
                <Input
                  placeholder="رقم الجوال (مثال: 05xxxxxxxx)"
                  value={reportCustomPhone}
                  onChange={(e) => setReportCustomPhone(e.target.value)}
                />
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-gray-800">نص التقرير</p>
                {!reportPreviewLoading && !reportPreviewError && reportMessage && (
                  <button
                    type="button"
                    className="text-sm font-medium text-primary-600 hover:text-primary-800"
                    onClick={() => setIsEditingReport((v) => !v)}
                  >
                    {isEditingReport ? 'إنهاء التعديل' : 'تعديل'}
                  </button>
                )}
              </div>

              {!reportTripId ? (
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-8 text-center text-sm text-gray-500">
                  اختر رحلة لعرض كشف التسكين
                </div>
              ) : reportPreviewLoading ? (
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-8 text-center text-sm text-gray-500">
                  جاري تحميل التقرير...
                </div>
              ) : reportPreviewError ? (
                <div className="rounded-lg bg-red-50 text-red-800 text-sm px-3 py-2">
                  {reportPreviewError}
                </div>
              ) : isEditingReport ? (
                <Textarea
                  rows={14}
                  value={reportMessage}
                  onChange={(e) => setReportMessage(e.target.value)}
                  className="font-mono text-sm leading-relaxed"
                />
              ) : reportMeta ? (
                <div className="max-h-80 overflow-y-auto">
                  {renderHousingReportSheet(false)}
                </div>
              ) : (
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-800 whitespace-pre-wrap leading-relaxed max-h-72 overflow-y-auto">
                  {reportMessage || '—'}
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-3 justify-end pt-2">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeReportModal}
              >
                إلغاء
              </button>
              <button
                type="button"
                className="btn-secondary inline-flex items-center gap-2"
                disabled={
                  (!reportMeta?.rooms?.length && !reportMessage.trim()) ||
                  reportPreviewLoading ||
                  !!reportPreviewError
                }
                onClick={handlePrintReport}
              >
                <PrinterIcon className="h-5 w-5" />
                طباعة
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={
                  !reportTripId ||
                  selectedReportRecipients.length === 0 ||
                  !reportMessage.trim() ||
                  reportPreviewLoading ||
                  !!reportPreviewError ||
                  sendReportMutation.isPending ||
                  (reportRecipients.custom && !reportCustomPhone.trim())
                }
                onClick={() =>
                  sendReportMutation.mutate({
                    tripId: reportTripId,
                    busId: reportBusId || undefined,
                    selected: selectedReportRecipients,
                    customPhone: reportCustomPhone,
                    message: reportMessage,
                  })
                }
              >
                {sendReportMutation.isPending
                  ? 'جاري الإرسال...'
                  : 'تأكيد إرسال التقرير'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showReport &&
        reportMeta &&
        createPortal(
          <div className="housing-report-print-root hidden print:block" aria-hidden="true">
            {renderHousingReportSheet(true)}
          </div>,
          document.body
        )}
    </div>
  );
};

export default HousingPage;
